/**
 * Unified Status & Action Decision Engine (Koredor)
 *
 * Deterministic, consistent rules applied identically across
 * Growth Channels and Campaigns. Never applied to raw
 * Job-Level Attribution tables.
 *
 * Evaluation priority: Lead Quality → Net Conversion → CPA → Volume
 */

/* ── Types ── */

export type DecisionStatus = "Healthy" | "Needs Attention" | "Underperforming";
export type DecisionAction = "Scale" | "Monitor" | "Review" | "Pause";

export interface DecisionInput {
  /** Net Conversion % (0-100) */
  netConversionPct: number;
  /** Net CPA in dollars */
  cpa: number;
  /** Lead Quality % (0-100) — percentage of valid/high-quality leads */
  leadQualityPct: number;
  /** Total job volume */
  jobs: number;
  /** Whether the channel/campaign is active (default true) */
  active?: boolean;
}

export interface DecisionResult {
  status: DecisionStatus;
  action: DecisionAction;
  descriptor: string;
}

/* ── Thresholds ── */

const THRESHOLDS = {
  conversion: { healthy: 20, attention: 10 },
  quality: { healthy: 70, attention: 50 },
  volume: { minimum: 3 },
  cpa: { acceptable: 400 },
} as const;

/* ── Status Derivation ── */

export function deriveStatus(input: DecisionInput): DecisionStatus {
  const { netConversionPct, leadQualityPct, cpa, jobs, active = true } = input;

  if (!active) return "Needs Attention";

  // Evaluate in priority order: Quality → Conversion → CPA → Volume
  // UNDERPERFORMING — any hard failure
  if (leadQualityPct < THRESHOLDS.quality.attention) return "Underperforming";
  if (netConversionPct < THRESHOLDS.conversion.attention) return "Underperforming";
  if (cpa > THRESHOLDS.cpa.acceptable * 1.5) return "Underperforming"; // significantly above

  // HEALTHY — all metrics strong
  if (
    leadQualityPct >= THRESHOLDS.quality.healthy &&
    netConversionPct >= THRESHOLDS.conversion.healthy &&
    jobs >= THRESHOLDS.volume.minimum
  ) {
    return "Healthy";
  }

  // Everything else
  return "Needs Attention";
}

/* ── Action Derivation ── */

export function deriveAction(input: DecisionInput): DecisionAction {
  const status = deriveStatus(input);
  const { netConversionPct, leadQualityPct, cpa, jobs, active = true } = input;

  if (!active) return "Pause";

  if (status === "Underperforming") return "Pause";

  if (status === "Healthy") {
    // Strong across ALL metrics → Scale
    if (
      netConversionPct >= 30 &&
      leadQualityPct >= 75 &&
      cpa <= THRESHOLDS.cpa.acceptable * 0.75
    ) {
      return "Scale";
    }
    return "Monitor";
  }

  // Needs Attention — determine if it's a quality or conversion issue
  if (leadQualityPct < THRESHOLDS.quality.healthy || netConversionPct < THRESHOLDS.conversion.healthy) {
    return "Review";
  }
  if (jobs < THRESHOLDS.volume.minimum) return "Monitor";

  return "Monitor";
}

/* ── Descriptor ── */

export function deriveDescriptor(input: DecisionInput): string {
  const status = deriveStatus(input);
  const { netConversionPct, leadQualityPct, cpa, active = true } = input;

  if (!active) return "Channel paused";

  if (status === "Healthy" && netConversionPct >= 30) return "Strong conversion";
  if (status === "Healthy") return "Solid performer";

  if (status === "Needs Attention") {
    if (cpa > THRESHOLDS.cpa.acceptable) return "High acquisition cost";
    if (leadQualityPct < THRESHOLDS.quality.healthy) return "Lead quality below target";
    return "Mixed signals — monitor";
  }

  if (netConversionPct < THRESHOLDS.conversion.attention) return "Low conversion — review needed";
  if (leadQualityPct < THRESHOLDS.quality.attention) return "Poor lead quality";
  return "Underperforming — review needed";
}

/* ── Combined convenience ── */

export function deriveDecision(input: DecisionInput): DecisionResult {
  return {
    status: deriveStatus(input),
    action: deriveAction(input),
    descriptor: deriveDescriptor(input),
  };
}

/* ── Badge styling helpers (consistent across modules) ── */

export function getStatusStyle(status: DecisionStatus) {
  switch (status) {
    case "Healthy":
      return { dotClass: "bg-success/60", textClass: "text-success", borderClass: "border-success/25" };
    case "Needs Attention":
      return { dotClass: "bg-warning/60", textClass: "text-warning", borderClass: "border-warning/25" };
    case "Underperforming":
      return { dotClass: "bg-destructive", textClass: "text-destructive", borderClass: "border-destructive/25" };
  }
}

export function getActionStyle(action: DecisionAction) {
  switch (action) {
    case "Scale":
      return { bg: "bg-success/10", text: "text-success", border: "border-success/20" };
    case "Monitor":
      return { bg: "bg-info/10", text: "text-info", border: "border-info/20" };
    case "Review":
      return { bg: "bg-warning/10", text: "text-warning", border: "border-warning/20" };
    case "Pause":
      return { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/20" };
  }
}

/* ── Insight text for expanded views ── */

export function deriveInsightText(input: DecisionInput): string {
  const status = deriveStatus(input);
  const { netConversionPct, leadQualityPct, cpa } = input;

  if (status === "Underperforming")
    return `Current CPA of $${Math.round(cpa)} exceeds thresholds while lead quality sits at ${Math.round(leadQualityPct)}%. Consider pausing or reviewing targeting strategy.`;
  if (leadQualityPct < 70)
    return `Lead quality at ${Math.round(leadQualityPct)}% is below the 70% benchmark. Monitor for another cycle before adjusting budget.`;
  if (status === "Healthy" && leadQualityPct >= 75)
    return `Strong performance with $${Math.round(cpa)} CPA and ${Math.round(leadQualityPct)}% quality. Consider scaling budget by 15-20%.`;
  if (netConversionPct < 20)
    return `Net conversion at ${Math.round(netConversionPct)}% is below the 20% target. Review lead sources and funnel quality.`;
  return `Metrics are within normal operating range. Continue monitoring weekly.`;
}
